import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import PhoneInput from "../common/phoneInput/phoneInput";
import imageCompression from "browser-image-compression";
import translations from "../../translations/translations";
import "./registerShop.css";
import companyBasedRequest from "../../request/companyBasedRequest";
import { Camera, UserCircle2 } from "lucide-react";
import CATEGORY_OPTIONS from "../../constants/categoryOptions";
import TermsOfServiceModal from "../common/termsOfServiceModal/termsOfServiceModal";
import PasswordField from "../common/passwordField/passwordField";
import RegisterPageSkeleton from "./registerShopSkeleton/registerShopSkeleton";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";

const RegisterShopAndAdminFlow = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const navigate = useNavigate();

    const [rememberMe, setRememberMe] = useState(false);
    const [inviteValid, setInviteValid] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [adminId, setAdminId] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [adminImage, setAdminImage] = useState(null);
    const [category, setCategory] = useState(""); // Beauty, Self-Care, etc.
    const [subCategory, setSubCategory] = useState("");
    const dropdownRef = useRef(null);
    const [openDropdown, setOpenDropdown] = useState(false);
    const [referralCode, setReferralCode] = useState("");
    const [activeModal, setActiveModal] = useState(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [banner, setBanner] = useState(null);
    const [exiting, setExiting] = useState(false);

    const fileInputRef = useRef(null);
    const maxSlots = 5;

    const { userData, setUserData, language } = useContext(CombinedContext);

    const showBanner = (type, text, duration = 2500) => {
        setBanner({ type, text });
        setExiting(false);

        setTimeout(() => {
            setExiting(true);
        }, duration - 250);

        setTimeout(() => {
            setBanner(null);
            setExiting(false);
        }, duration);
    };

    useEffect(() => {
        // if (userData?.user?.role === "admin") return;

        const verifyInvite = async () => {
            try {
                const res = await companyBasedRequest.verifyShopInvite(token);

                setEmail(res.email || "");
                setFirstName(res.firstName || "");
                setLastName(res.lastName || "");
                setPhone(res.phone || "");
                setCategory(res.category || "");
                setSubCategory(res.subCategory || "");

                setReferralCode(res.referralCode || "");

                setInviteValid(true);

                if (res.adminVerified && !res.used) {
                    if (!userData || !userData.cognitoTokens?.IdToken) {
                        navigate("/signin");
                        return;
                    }
                }

                // ✅ Mark as verified
                sessionStorage.setItem("inviteVerified", "true");
            } catch (err) {
                showBanner(
                    "error",
                    language === "es" ? "Enlace de invitación inválido o expirado" : "Invalid or expired invite link"
                );
            } finally {
                setVerifying(false);
            }
        };

        verifyInvite();
    }, [token]);

    useEffect(() => {
        if (userData && userData.user.role === "owner" && userData.user.onboardingStatus === "pending-shop") {
            setAdminId(userData.userId);
        }
    }, [userData]);

    if (verifying) {
        return <RegisterPageSkeleton />;
    }

    function isStrongPassword(password) {
        return (
            typeof password === "string" &&
            password.length >= 8 &&
            /[A-Z]/.test(password) &&
            /[a-z]/.test(password) &&
            /[0-9]/.test(password) &&
            /[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(password)
        );
    }

    const handleRegisterAdmin = async () => {
        if (!firstName || !lastName || !phone || !password || !confirmPassword || !category || !subCategory) {
            showBanner(
                "error",
                language === "es"
                    ? "Por favor completa todos los campos obligatorios"
                    : "Please fill out all required fields"
            );
            return;
        }

        if (!acceptedTerms) {
            showBanner("error", translations[language].registerShop.errors.mustAcceptTerms);
            return;
        }

        if (!isStrongPassword(password)) {
            showBanner(
                "error",
                language === "es"
                    ? "La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial"
                    : "Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character"
            );
            return;
        }

        if (password !== confirmPassword) {
            showBanner("error", language === "es" ? "Las contraseñas no coinciden" : "Passwords do not match");
            return;
        }

        setLoading(true);

        try {
            const payload = {
                token,
                password,
                firstName,
                lastName,
                phone: phone,
                category,
                subCategory,
                referralCode: referralCode || null,
                acceptedTermsVersion: "1.0",
            };

            const res = await companyBasedRequest.registerAdmin(payload);

            // 🧠 Normalize structure to match login and context
            const formattedData = {
                ...res,
                cognitoTokens: {
                    IdToken: res.cognitoTokens?.IdToken || res.IdToken,
                    AccessToken: res.cognitoTokens?.AccessToken || res.AccessToken,
                    RefreshToken: res.cognitoTokens?.RefreshToken || res.RefreshToken,
                },
            };

            setUserData(formattedData);
            setAdminId(res.adminId);
            navigate("/calendar");

            // 💾 Store tokens & user persistently
            const storage = rememberMe ? localStorage : sessionStorage;
            if (formattedData.cognitoTokens?.IdToken) storage.setItem("idToken", formattedData.cognitoTokens.IdToken);
            if (formattedData.cognitoTokens?.AccessToken)
                storage.setItem("accessToken", formattedData.cognitoTokens.AccessToken);
            if (formattedData.cognitoTokens?.RefreshToken)
                storage.setItem("refreshToken", formattedData.cognitoTokens.RefreshToken);
            storage.setItem("user", JSON.stringify(formattedData.user));
        } catch (err) {
            console.error("Register admin failed:", err);
            showBanner(
                "error",
                err.message || (language === "es" ? "Error al registrar administrador" : "Failed to register admin")
            );
        } finally {
            setLoading(false);
        }
    };

    const getLabel = (item, language) => {
        if (!item) return "";
        return language === "es" ? item.labelES : item.labelEN;
    };

    const selectedCategoryObj = CATEGORY_OPTIONS.find((c) => c.value === category);
    const availableSubCategories = selectedCategoryObj?.subCategories || [];

    return (
        <div className="container">
            <div className="card" style={{ maxWidth: 400, margin: "40px auto" }}>
                <div className="bd">
                    {success && (
                        <div className="sub" style={{ color: "green", marginBottom: 10 }}>
                            {success}
                        </div>
                    )}

                    <>
                        <div className="row" style={{ marginBottom: 10 }}>
                            <div className="sf-registershop-title">
                                {translations[language].registerShop.step1.title}
                            </div>
                            <input className="input" type="email" value={email} disabled />
                            <input
                                className="input"
                                placeholder={translations[language].registerShop.step1.firstName}
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                style={{ marginRight: 5 }}
                            />
                            <input
                                className="input"
                                placeholder={translations[language].registerShop.step1.lastName}
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />

                            <PhoneInput businessPhone={phone} setBusinessPhone={setPhone} />

                            <PasswordField
                                value={password}
                                onChange={setPassword}
                                placeholder={translations[language].registerShop.step1.password}
                                autoComplete="new-password"
                            />

                            <PasswordField
                                value={confirmPassword}
                                onChange={setConfirmPassword}
                                placeholder={translations[language].registerShop.step1.confirmPassword}
                                autoComplete="new-password"
                            />

                            <input
                                className="input"
                                placeholder={translations[language].registerShop.step1.referralCode}
                                value={referralCode}
                                onChange={(e) => setReferralCode(e.target.value)}
                            />

                            

                            {/* <div className="sub" style={{ marginBottom: 10, fontWeight: "500" }}>
                                {translations[language].registerShop.step1.category || "Business category"}
                            </div> */}

                            <div className="category-input-container" ref={dropdownRef}>
                                <button
                                    type="button"
                                    className="category-dropdown-btn"
                                    onClick={() => setOpenDropdown(openDropdown === "category" ? null : "category")}
                                >
                                    {category
                                        ? language === "es"
                                            ? selectedCategoryObj.labelES
                                            : selectedCategoryObj.labelEN
                                        : language === "es"
                                        ? "Tipo de negocio"
                                        : "Business category"}
                                    <span className={`arrow ${openDropdown === "category" ? "open" : ""}`}>▼</span>
                                </button>

                                {openDropdown === "category" && (
                                    <ul className="category-dropdown-list">
                                        {CATEGORY_OPTIONS.map((cat) => (
                                            <li
                                                key={cat.value}
                                                className="category-dropdown-item"
                                                onClick={() => {
                                                    setCategory(cat.value);
                                                    setSubCategory("");
                                                    setOpenDropdown(null);
                                                }}
                                            >
                                                {language === "es" ? cat.labelES : cat.labelEN}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="category-input-container">
                                <button
                                    type="button"
                                    className="category-dropdown-btn"
                                    onClick={() =>
                                        setOpenDropdown(openDropdown === "subcategory" ? null : "subcategory")
                                    }
                                >
                                    {subCategory
                                        ? language === "es"
                                            ? availableSubCategories.find((s) => s.value === subCategory)?.labelES
                                            : availableSubCategories.find((s) => s.value === subCategory)?.labelEN
                                        : language === "es"
                                        ? "Subcategoría"
                                        : "Subcategory"}
                                    <span className={`arrow ${openDropdown === "subcategory" ? "open" : ""}`}>▼</span>
                                </button>

                                {openDropdown === "subcategory" && (
                                    <ul className="category-dropdown-list">
                                        {availableSubCategories.map((sub) => (
                                            <li
                                                key={sub.value}
                                                className="category-dropdown-item"
                                                onClick={() => {
                                                    setSubCategory(sub.value);
                                                    setOpenDropdown(null);
                                                }}
                                            >
                                                {language === "es" ? sub.labelES : sub.labelEN}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        {/* )} */}

                        <div
                            className="row sf-register-terms-remember-me"
                            style={{
                                marginBottom: 10,
                            }}
                        >
                            <div
                                // className="sf-register-terms-remember-me"
                                style={{
                                    // marginBottom: 10,
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <input
                                    type="checkbox"
                                    id="rememberMe"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    style={{ marginRight: 6 }}
                                />
                                <label htmlFor="rememberMe">{translations[language].registerShop.step1.remember}</label>
                            </div>

                            <div className="terms-checkbox">
                                <label className="terms-label">
                                    <input
                                        type="checkbox"
                                        checked={acceptedTerms}
                                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                                        style={{ marginRight: 6 }}
                                    />
                                    <span>
                                        {translations[language].register.acceptTerms}{" "}
                                        <span className="sf-terms-span" onClick={() => setActiveModal("terms")}>
                                            {translations[language].register.termsLink}
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </div>

                        {banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}

                        <button
                            className="btn brand"
                            style={{ width: "100%", marginBottom: 10 }}
                            onClick={handleRegisterAdmin}
                            disabled={loading}
                        >
                            {loading
                                ? translations[language].registerShop.step1.registering
                                : translations[language].registerShop.step1.register}
                        </button>
                    </>
                </div>
            </div>
            {activeModal === "terms" && <TermsOfServiceModal isOpen onClose={() => setActiveModal(null)} />}
        </div>
    );
};

export default RegisterShopAndAdminFlow;
